import type { ReactNode } from 'react';

import type {
  BusSegment,
  FlightSegment,
  RentalAgency,
  VoucherView,
} from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { Kv, KvGrid } from '@web/ui/core/molecules';
import { formatDateOrNull, formatDuration, formatRawDateTime } from '@web/ui/core/utils';

/**
 * The voucher drawer's detail body: a "Reserva" key/value section (booking meta,
 * always present) followed by a per-modal section keyed on `details.kind`. Each
 * section is a 1-column `KvGrid` under a mono eyebrow label. Absent values render
 * a labelled faint text ("Não informado", and field-specific labels like "Não
 * emitida" / "Não atribuído"), never a bare dash. Shared by the customer and MICE voucher drawers.
 */
export function VoucherDetailSections({
  voucher,
}: Readonly<{ voucher: VoucherView }>): React.JSX.Element {
  return (
    <>
      <ReservationSection voucher={voucher} />
      <PendingApproversSection approvers={voucher.pendingApprovers} />
      <DetailSection details={voucher.details} />
    </>
  );
}

function DrawerSection({
  label,
  children,
}: Readonly<{ label: string; children: ReactNode }>): React.JSX.Element {
  return (
    <section className="flex flex-col gap-2">
      <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
        {label}
      </span>
      <KvGrid oneColumn>{children}</KvGrid>
    </section>
  );
}

/**
 * One label/value cell that renders its labelled-empty text (faint) when the
 * value is absent, instead of a bare dash.
 */
function Field({
  label,
  value,
  mono,
  emptyLabel,
}: Readonly<{
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  emptyLabel?: string;
}>): React.JSX.Element {
  const empty = !value;
  return (
    <Kv label={label} mono={mono && !empty} empty={empty}>
      {empty ? (emptyLabel ?? t('customers.empty.notInformed')) : value}
    </Kv>
  );
}

function ReservationSection({ voucher }: Readonly<{ voucher: VoucherView }>): React.JSX.Element {
  return (
    <DrawerSection label={t('customers.voucher.detail.section.reservation')}>
      <Field label={t('customers.voucher.detail.internalStatus')} value={voucher.internalStatus} />
      <Field
        label={t('customers.voucher.detail.requestedAt')}
        value={formatDateOrNull(voucher.createdAt)}
      />
      <Field label={t('customers.voucher.detail.supplier')} value={voucher.supplier} />
      <Field
        label={t('customers.voucher.detail.reservationNumber')}
        value={voucher.reservationNumber}
        mono
        emptyLabel={t('customers.empty.notIssued')}
      />
      <Field
        label={t('customers.voucher.detail.emittedAt')}
        value={formatDateOrNull(voucher.emittedAt)}
      />
      <Field label={t('customers.voucher.detail.requester')} value={voucher.requester?.name} />
    </DrawerSection>
  );
}

function PendingApproversSection({
  approvers,
}: Readonly<{ approvers: VoucherView['pendingApprovers'] }>): React.JSX.Element | null {
  if (!approvers?.length) return null;
  return (
    <DrawerSection label={t('customers.voucher.detail.section.pendingApprovers')}>
      <ul className="flex flex-col gap-1 px-4 py-2.75">
        {approvers.map((approver, index) => (
          // Names are not unique ids; the list is static per render.
          <li
            key={`${approver.name}-${String(index)}`}
            className="break-words text-card-meta leading-card-meta text-foreground"
          >
            {approver.name}
          </li>
        ))}
      </ul>
    </DrawerSection>
  );
}

function DetailSection({
  details,
}: Readonly<{ details: VoucherView['details'] }>): React.JSX.Element | null {
  if (!details) return null;
  switch (details.kind) {
    case 'HOTEL':
      return <HotelSection details={details} />;
    case 'FLIGHT':
      return <FlightSection segments={details.segments} />;
    case 'BUS':
      return <BusSection segments={details.segments} />;
    case 'RENTAL':
      return <RentalSection details={details} />;
    case 'MOBILITY':
      return <MobilitySection details={details} />;
    case 'EXPENSE':
    case 'EXPENSE_KM':
      return null; // refunds have no section here (#5034)
    case 'PAYMENT':
      return null; // the backoffice shows the payment through the common fields
    default:
      return unhandledCase(details);
  }
}

/** A detail kind left unhandled fails to COMPILE; a value outside the type at runtime renders
 *  nothing rather than handing React a raw object. */
function unhandledCase(_value: never): null {
  return null;
}

function HotelSection({
  details,
}: Readonly<{ details: Extract<VoucherView['details'], { kind: 'HOTEL' }> }>): React.JSX.Element {
  return (
    <DrawerSection label={t('customers.voucher.detail.section.hotel')}>
      <Field label={t('customers.voucher.detail.hotel.name')} value={details.name} />
      <Field label={t('customers.voucher.detail.hotel.city')} value={details.city} />
      <Field label={t('customers.voucher.detail.hotel.address')} value={details.address} />
      <Field
        label={t('customers.voucher.detail.hotel.checkIn')}
        value={dateWithHour(details.checkIn, details.checkInHour)}
      />
      <Field
        label={t('customers.voucher.detail.hotel.checkOut')}
        value={dateWithHour(details.checkOut, details.checkOutHour)}
      />
      <Field
        label={t('customers.voucher.detail.hotel.nights')}
        value={numberText(details.nights)}
      />
      <Field
        label={t('customers.voucher.detail.hotel.guests')}
        value={numberText(details.guests)}
      />
      <Field label={t('customers.voucher.detail.hotel.rooms')} value={numberText(details.rooms)} />
      <Field
        label={t('customers.voucher.detail.hotel.roomDescription')}
        value={details.roomDescription}
      />
    </DrawerSection>
  );
}

/** Stable per-segment key from the segment's own leg/route/time fields (not the render index). */
function flightSegmentKey(segment: FlightSegment): string {
  return [
    segment.type,
    segment.departureAirport,
    segment.arrivalAirport,
    segment.departAt?.toISOString(),
    segment.flightNumbers.join(','),
  ].join('|');
}

function FlightSection({
  segments,
}: Readonly<{ segments: readonly FlightSegment[] }>): React.JSX.Element {
  return (
    <DrawerSection label={t('customers.voucher.detail.section.flight')}>
      {segments.map((segment, i) => (
        <FlightSegmentRows key={flightSegmentKey(segment)} segment={segment} index={i} />
      ))}
    </DrawerSection>
  );
}

function FlightSegmentRows({
  segment,
  index,
}: Readonly<{ segment: FlightSegment; index: number }>): React.JSX.Element {
  return (
    <>
      <Field
        label={t('customers.voucher.detail.flight.segment', { n: index + 1 })}
        value={segment.airline}
      />
      <Field
        label={t('customers.voucher.detail.flight.depart')}
        value={place(
          segment.departureAirport,
          formatRawDateTime(segment.departLocal, segment.departAt),
        )}
      />
      <Field
        label={t('customers.voucher.detail.flight.arrive')}
        value={place(
          segment.arrivalAirport,
          formatRawDateTime(segment.arriveLocal, segment.arriveAt),
        )}
      />
      <Field
        label={t('customers.voucher.detail.flight.duration')}
        value={formatDuration(segment.duration)}
      />
      <Field label={t('customers.voucher.detail.flight.stops')} value={numberText(segment.stops)} />
      <Field label={t('customers.voucher.detail.flight.cabinClass')} value={segment.cabinClass} />
      <Field
        label={t('customers.voucher.detail.flight.seat')}
        value={segment.seat}
        emptyLabel={t('customers.empty.noSeat')}
      />
      <Field
        label={t('customers.voucher.detail.flight.baggage')}
        value={segment.baggage}
        emptyLabel={t('customers.empty.noBaggage')}
      />
      <Field
        label={t('customers.voucher.detail.flight.locator')}
        value={locator(segment.flightNumbers, segment.locator)}
        mono
      />
    </>
  );
}

/** Stable per-segment key from the segment's own leg/route/time fields (not the render index). */
function busSegmentKey(segment: BusSegment): string {
  return [
    segment.type,
    segment.departureStation,
    segment.arrivalStation,
    segment.departAt?.toISOString(),
    segment.company,
  ].join('|');
}

function BusSection({
  segments,
}: Readonly<{ segments: readonly BusSegment[] }>): React.JSX.Element {
  return (
    <DrawerSection label={t('customers.voucher.detail.section.bus')}>
      {segments.map((segment, i) => (
        <BusSegmentRows key={busSegmentKey(segment)} segment={segment} index={i} />
      ))}
    </DrawerSection>
  );
}

function BusSegmentRows({
  segment,
  index,
}: Readonly<{ segment: BusSegment; index: number }>): React.JSX.Element {
  return (
    <>
      <Field
        label={t('customers.voucher.detail.bus.segment', { n: index + 1 })}
        value={segment.company}
      />
      <Field
        label={t('customers.voucher.detail.bus.depart')}
        value={place(segment.departureStation, formatRawDateTime(null, segment.departAt))}
      />
      <Field
        label={t('customers.voucher.detail.bus.arrive')}
        value={place(segment.arrivalStation, formatRawDateTime(null, segment.arriveAt))}
      />
      <Field
        label={t('customers.voucher.detail.bus.seat')}
        value={segment.seat}
        emptyLabel={t('customers.empty.noSeat')}
      />
      <Field label={t('customers.voucher.detail.bus.seatClass')} value={segment.seatClass} />
      <Field label={t('customers.voucher.detail.bus.locator')} value={segment.locator} mono />
    </>
  );
}

function RentalSection({
  details,
}: Readonly<{ details: Extract<VoucherView['details'], { kind: 'RENTAL' }> }>): React.JSX.Element {
  return (
    <DrawerSection label={t('customers.voucher.detail.section.rental')}>
      <Field
        label={t('customers.voucher.detail.rental.pickup')}
        value={agencyText(details.pickupAgency)}
      />
      <Field
        label={t('customers.voucher.detail.rental.dropoff')}
        value={agencyText(details.dropoffAgency)}
      />
      <Field label={t('customers.voucher.detail.rental.carModel')} value={details.carModel} />
      <Field label={t('customers.voucher.detail.rental.carGroup')} value={details.carGroup} />
    </DrawerSection>
  );
}

function MobilitySection({
  details,
}: Readonly<{
  details: Extract<VoucherView['details'], { kind: 'MOBILITY' }>;
}>): React.JSX.Element {
  return (
    <DrawerSection label={t('customers.voucher.detail.section.mobility')}>
      <Field label={t('customers.voucher.detail.mobility.category')} value={details.category} />
      <Field
        label={t('customers.voucher.detail.mobility.distance')}
        value={details.distance == null ? null : `${String(details.distance)} km`}
      />
      <Field
        label={t('customers.voucher.detail.mobility.duration')}
        value={formatDuration(details.duration)}
      />
    </DrawerSection>
  );
}

function numberText(value: number | null | undefined): string | null {
  return value == null ? null : String(value);
}

/** A check-in/out day with the source-provided hour appended when present. */
function dateWithHour(
  day: Date | null | undefined,
  hour: string | null | undefined,
): string | null {
  const head = formatDateOrNull(day);
  if (!head) return null;
  return hour ? `${head} ${hour}` : head;
}

/** "<IATA/station> · <when>", dropping whichever side is absent; null if both are. */
function place(where: string | null, when: string): string | null {
  const parts = [where, when === '—' ? null : when].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** "<PNR> · <flight numbers>" joined with the editorial middot, never a raw comma. */
function locator(flightNumbers: readonly string[], pnr: string | null): string | null {
  const numbers = flightNumbers.length > 0 ? flightNumbers.join(' · ') : null;
  const parts = [pnr, numbers].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** "<name> · <address> · <city>" for a rental agency, dropping absent parts. */
function agencyText(agency: RentalAgency | null): string | null {
  if (!agency) return null;
  const text = [agency.name, agency.address, agency.city].filter(Boolean).join(' · ');
  return text || null;
}
